import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Linkedin, Youtube, ArrowUpRight } from "lucide-react";

export default function Footer() {
  return (
    <footer data-testid="main-footer" className="bg-[#0e5f84] text-white/70 relative overflow-hidden border-t border-white/15">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-20 pb-8">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="bg-white inline-block p-3 border border-white/15">
              <img src="/assets/manik-logo.png" alt="Manik Engineering" className="h-16 w-auto" />
            </div>
            <p className="mt-6 text-white/70 max-w-md leading-relaxed">
              Engineering modular workspaces, warehouse safety, and industrial storage
              for India&apos;s leading manufacturers since day one.
            </p>
          </div>

          <div className="lg:col-span-3">
            <p className="font-mono-accent text-xs uppercase tracking-widest text-[#78be27] mb-6">Navigate</p>
            <ul className="space-y-3">
              {[["Home", "/"], ["Products", "/products"], ["Projects", "/projects"], ["Videos", "/videos"], ["About", "/about"], ["Contact", "/contact"]].map(
                ([label, to]) => (
                  <li key={to}>
                    <Link to={to} className="text-white/70 hover:text-[#78be27] transition-colors duration-300">
                      {label}
                    </Link>
                  </li>
                )
              )}
            </ul>
          </div>

          <div className="lg:col-span-4">
            <p className="font-mono-accent text-xs uppercase tracking-widest text-[#78be27] mb-6">Reach Us</p>
            <ul className="space-y-4 text-white/70">
              <li className="flex items-start gap-3">
                <Mail size={18} className="text-[#78be27] mt-1 shrink-0" />
                <a href="mailto:sales@manikindia.com" className="hover:text-[#78be27] transition-colors">sales@manikindia.com</a>
              </li>
              <li className="flex items-start gap-3">
                <Phone size={18} className="text-[#78be27] mt-1 shrink-0" />
                <div className="flex flex-col">
                  <a href="tel:+918424040899" className="hover:text-[#78be27] transition-colors">+91 84240 40899</a>
                  <a href="tel:+919845172851" data-testid="footer-phone-secondary" className="hover:text-[#78be27] transition-colors">+91 98451 72851</a>
                </div>
              </li>
              <li className="flex items-start gap-3" data-testid="footer-address-corporate">
                <MapPin size={18} className="text-[#78be27] mt-1 shrink-0" />
                <div className="text-sm leading-relaxed">
                  <span className="font-semibold text-white block mb-1">Corporate Office:</span>
                  Ground &amp; Mezz. Floor, Lakhi Industry House, LBS Marg, Bhandup (W), Mumbai - 400078
                </div>
              </li>
              <li className="flex items-start gap-3" data-testid="footer-address-factory">
                <MapPin size={18} className="text-[#78be27] mt-1 shrink-0" />
                <div className="text-sm leading-relaxed">
                  <span className="font-semibold text-white block mb-1">Factory:</span>
                  A-805/1, Sector 2, Kopar Khairane, Navi Mumbai, Maharashtra 400710
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* FOLLOW US band */}
        <div data-testid="footer-social" className="mt-16 border-y border-white/15 py-10 flex items-center justify-between gap-8 flex-wrap">
          <div>
            <p className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-[#3da8d8] mb-2">Follow Us</p>
            <h3 className="font-display font-extrabold text-white text-2xl lg:text-3xl leading-tight">
              See what we&apos;re building next.
            </h3>
          </div>
          <div className="flex items-center gap-4">
            <a
              href="https://in.linkedin.com/company/manik-engineering-pvt-ltd"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-linkedin"
              aria-label="Manik Engineering on LinkedIn"
              className="group flex items-center gap-3 border border-white/20 hover:border-[#0A66C2] hover:bg-[#0A66C2] px-5 py-4 transition-colors"
            >
              <span className="w-10 h-10 rounded-full bg-[#0A66C2] group-hover:bg-white/20 flex items-center justify-center transition-colors">
                <Linkedin size={20} className="text-white" strokeWidth={2} />
              </span>
              <div className="text-left">
                <p className="font-mono-accent text-[10px] uppercase tracking-widest text-white/60 group-hover:text-white/90">Company page</p>
                <p className="font-display font-bold text-white inline-flex items-center gap-1.5">LinkedIn <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" /></p>
              </div>
            </a>
            <a
              href="https://www.youtube.com/@MEPL_India"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-youtube"
              aria-label="Manik Engineering on YouTube"
              className="group flex items-center gap-3 border border-white/20 hover:border-[#FF0000] hover:bg-[#FF0000] px-5 py-4 transition-colors"
            >
              <span className="w-10 h-10 rounded-full bg-[#FF0000] group-hover:bg-white/20 flex items-center justify-center transition-colors">
                <Youtube size={20} className="text-white" strokeWidth={2} />
              </span>
              <div className="text-left">
                <p className="font-mono-accent text-[10px] uppercase tracking-widest text-white/60 group-hover:text-white/90">MEPL India</p>
                <p className="font-display font-bold text-white inline-flex items-center gap-1.5">YouTube <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" /></p>
              </div>
            </a>
          </div>
        </div>

        <div className="pt-8 flex flex-col sm:flex-row justify-between gap-4 text-white/50 text-sm">
          <p>© {new Date().getFullYear()} Manik Engineering Pvt. Ltd. All rights reserved.</p>
          <Link to="/admin" data-testid="footer-admin-link" className="font-mono-accent text-xs uppercase tracking-widest hover:text-[#78be27] transition-colors">
            Admin
          </Link>
        </div>
      </div>
    </footer>
  );
}
